import { localeLink, tr, useStoreLanguage } from '@/i18n';
import { LANGUAGE_TAGS, localizedUrl, splitLocalePath } from '@/i18n/core';
import { useEffect } from 'react';
import { STORE_CONFIG } from '@/config/storeConfig';
import { seoDescription } from './seoText';


interface SEOProps {
  enabled?: boolean;
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageType?: string;
  schema?: object;
  noindex?: boolean;
  ogPhone?: string;
  ogEmail?: string;
  hreflang?: Array<{ lang: string; url: string }>;
  /** Pagination: URL of the previous page (rel="prev") */
  prevUrl?: string;
  /** Pagination: URL of the next page (rel="next") */
  nextUrl?: string;
  /** Additional og:locale:alternate values (e.g. ['es_MX', 'es_AR']) */
  ogLocaleAlternate?: string[];
  /**
   * Article publish date — ISO 8601 string (e.g. "2026-04-08T10:00:00Z")
   * Injects article:published_time + article:modified_time OG tags.
   * Only used when ogType === 'article'.
   */
  articlePublishedTime?: string;
  /** Article last modified date — ISO 8601 string. Falls back to articlePublishedTime. */
  articleModifiedTime?: string;
  /** Article section/category for og:article:section */
  articleSection?: string;
  /** Article tags for og:article:tag (max 6 recommended by Facebook) */
  articleTags?: string[];
}

const DEFAULT_OG_IMAGE = 'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/product-images/og-cover.jpg';

export const useSEO = ({
  enabled = true,
  title: rawTitle,
  description: rawDescription,
  keywords,
  canonical: rawCanonical,
  ogType = 'website',
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogImageType,
  schema,
  noindex = false,
  ogPhone,
  ogEmail,
  hreflang,
  prevUrl,
  nextUrl,
  ogLocaleAlternate,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
}: SEOProps) => {
  const language = useStoreLanguage();
  const title = tr(rawTitle);
  const description = seoDescription(tr(rawDescription));
  const canonical = rawCanonical ? localeLink(rawCanonical) : rawCanonical;
  useEffect(() => {
    if (!enabled) return;
    document.getElementById('server-page-schema')?.remove();
    // Update title
    document.title = title;
    // Track after the asynchronous page metadata is ready, never the previous title.
    const pageReadyTimer = window.setTimeout(() => window.dispatchEvent(new Event('solsazon:page-ready')), 0);

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    const removeMetaTag = (name: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      const el = document.querySelector(`meta[${attribute}="${name}"]`);
      if (el) el.remove();
    };

    // robots: noindex for pages we want out of the index but still crawlable
    // noindex,follow (NOT nofollow) — preserves link equity and internal crawl
    if (noindex) {
      updateMetaTag('robots', 'noindex, follow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Update description
    updateMetaTag('description', description);

    // Update keywords if provided
    if (keywords) {
      updateMetaTag('keywords', tr(keywords));
    }

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:locale', LANGUAGE_TAGS[language].replace('-', '_'), true);

    // WhatsApp Business / Open Graph contact tags
    if (ogPhone) {
      updateMetaTag('og:phone_number', ogPhone, true);
    } else {
      removeMetaTag('og:phone_number', true);
    }
    if (ogEmail) {
      updateMetaTag('og:email', ogEmail, true);
    } else {
      removeMetaTag('og:email', true);
    }

    // Update og:image and twitter:image (page-specific or default)
    const imageUrl = ogImage || DEFAULT_OG_IMAGE;
    updateMetaTag('og:image', imageUrl, true);

    // og:image dimensions and type — critical for Facebook and LinkedIn rich previews
    const imgWidth = ogImageWidth || (ogType === 'product' ? 800 : 1200);
    const imgHeight = ogImageHeight || (ogType === 'product' ? 800 : 630);
    const imgType = ogImageType || 'image/jpeg';
    updateMetaTag('og:image:width', String(imgWidth), true);
    updateMetaTag('og:image:height', String(imgHeight), true);
    updateMetaTag('og:image:type', imgType, true);
    updateMetaTag('og:image:alt', title, true);

    // Twitter Card — summary_large_image para mejor engagement
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:site', '@solsazon');
    updateMetaTag('twitter:creator', '@solsazon');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);
    updateMetaTag('twitter:image:alt', title);

    // ── article:* Open Graph tags — for Facebook, LinkedIn, WhatsApp previews ──
    // Remove stale article tags first (in case user navigates between article/non-article pages)
    [
      'article:published_time',
      'article:modified_time',
      'article:section',
      'article:author',
    ].forEach((prop) => removeMetaTag(prop, true));
    document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());

    if (ogType === 'article') {
      if (articlePublishedTime) {
        updateMetaTag('article:published_time', articlePublishedTime, true);
      }
      // modified_time falls back to published_time if not provided
      updateMetaTag(
        'article:modified_time',
        articleModifiedTime || articlePublishedTime || new Date().toISOString(),
        true
      );
      updateMetaTag('article:author', 'Equipo Solsazón', true);
      if (articleSection) {
        updateMetaTag('article:section', articleSection, true);
      }
      // article:tag — Facebook/LinkedIn use these for topic classification
      if (articleTags && articleTags.length > 0) {
        articleTags.slice(0, 6).forEach((tag) => {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'article:tag');
          meta.setAttribute('content', tag);
          document.head.appendChild(meta);
        });
      }
    }

    // Update canonical URL — normalize: strip query params, hash and trailing slash
    if (!noindex) {
      // Strip any query string or hash that may have been passed in
      const requested = new URL(canonical || window.location.pathname, 'https://www.sal-sazon.com');
      let cleanCanonical = 'https://www.sal-sazon.com' + requested.pathname;
      // Strip trailing slash except for root "/"
      if (cleanCanonical.length > 1 && cleanCanonical.endsWith('/')) {
        cleanCanonical = cleanCanonical.slice(0, -1);
      }

      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', cleanCanonical);
      updateMetaTag('og:url', cleanCanonical, true);
    } else if (noindex) {
      // Remove canonical on noindex pages
      const linkElement = document.querySelector('link[rel="canonical"]');
      if (linkElement) linkElement.remove();
    }

    // Modification dates belong to the content, not the time of every page view.

    // Reciprocal alternates point to the published language routes.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    const siteUrlForHreflang = (import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com').replace(/\/$/, '');

    if (!noindex && hreflang && hreflang.length > 0) {
      // Caller explicitly provided hreflang set — use it as-is
      hreflang.forEach(({ lang, url }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        link.setAttribute('href', url);
        document.head.appendChild(link);
      });
    } else if (canonical && !noindex) {
      getHreflangSet(canonical.replace(siteUrlForHreflang, '')).forEach(({ lang, url }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        link.setAttribute('href', url);
        document.head.appendChild(link);
      });
    }

    // ── rel="prev" / rel="next" — pagination signals for Google ──────────────
    // Remove stale pagination links from previous renders
    document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach((el) => el.remove());

    if (prevUrl && !noindex) {
      const prevLink = document.createElement('link');
      prevLink.setAttribute('rel', 'prev');
      prevLink.setAttribute('href', localeLink(prevUrl));
      document.head.appendChild(prevLink);
    }
    if (nextUrl && !noindex) {
      const nextLink = document.createElement('link');
      nextLink.setAttribute('rel', 'next');
      nextLink.setAttribute('href', localeLink(nextUrl));
      document.head.appendChild(nextLink);
    }

    // ── og:locale:alternate — for the available storefront languages ──────────────────
    // Remove stale og:locale:alternate tags
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());

    // Advertise only the languages available on the storefront.
    const localeAlternates = ogLocaleAlternate ?? Object.values(LANGUAGE_TAGS).filter(value => value !== LANGUAGE_TAGS[language]).map(value => value.replace('-', '_'));
    localeAlternates.forEach((locale) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:locale:alternate');
      meta.setAttribute('content', locale);
      document.head.appendChild(meta);
    });

    // NOTE: JSON-LD schema injection is handled by <SchemaScript> component
    // (src/components/base/SchemaScript.tsx) which uses createPortal to inject
    // the script synchronously in the React render cycle — before browser paint.
    // This ensures crawlers see the schema on first DOM snapshot.

    // Cleanup
    return () => {
      window.clearTimeout(pageReadyTimer);
      // Remove hreflang links on unmount
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      // Remove pagination links on unmount
      document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach((el) => el.remove());
      // Remove og:locale:alternate on unmount
      document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
      // Remove article:* tags on unmount
      ['article:published_time', 'article:modified_time', 'article:section', 'article:author'].forEach((prop) => {
        const el = document.querySelector(`meta[property="${prop}"]`);
        if (el) el.remove();
      });
      document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());

      const restoreDefault = (name: string, isProperty = false) => {
        const attribute = isProperty ? 'property' : 'name';
        const el = document.querySelector(`meta[${attribute}="${name}"]`);
        if (el) el.setAttribute('content', DEFAULT_OG_IMAGE);
      };
      restoreDefault('og:image', true);
      restoreDefault('twitter:image');

      // Reset og:image dimensions to defaults
      const resetMeta = (prop: string, value: string) => {
        const el = document.querySelector(`meta[property="${prop}"]`);
        if (el) el.setAttribute('content', value);
      };
      resetMeta('og:image:width', '1200');
      resetMeta('og:image:height', '630');
      resetMeta('og:image:type', 'image/jpeg');
      resetMeta('og:image:alt', 'Solsazón');

      // Restore robots to default
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.setAttribute('content', 'index, follow');


    };
  }, [language, enabled, title, description, keywords, canonical, ogType, ogImage, ogImageWidth, ogImageHeight, ogImageType, schema, noindex, ogPhone, ogEmail, hreflang, prevUrl, nextUrl, ogLocaleAlternate, articlePublishedTime, articleModifiedTime, articleSection, articleTags]);
};

const LOGO_URL = 'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/product-images/og-cover.jpg';

// Organization Schema (to be added globally) — enriched for Google Knowledge Panel
export const getOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Solsazón',
    alternateName: 'Solsazon',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      '@id': `${siteUrl}/#logo`,
      url: LOGO_URL,
      width: 1200,
      height: 630,
      caption: 'Solsazón — Especias y Hierbas Premium',
    },
    image: LOGO_URL,
    description: 'Tienda online de hierbas orgánicas, especias premium y mezclas artesanales. Envío desde Almería a toda España.',
    foundingDate: '2026',
    foundingLocation: {
      '@type': 'Place',
      name: `${STORE_CONFIG.GEO_CITY}, ${STORE_CONFIG.GEO_REGION}, ${STORE_CONFIG.GEO_COUNTRY}`,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: STORE_CONFIG.GEO_CITY,
      addressRegion: STORE_CONFIG.GEO_REGION,
      postalCode: STORE_CONFIG.GEO_POSTAL_CODE,
      addressCountry: STORE_CONFIG.GEO_COUNTRY_CODE,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: STORE_CONFIG.GEO_LAT,
      longitude: STORE_CONFIG.GEO_LNG,
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: STORE_CONFIG.CONTACT_PHONE_SCHEMA,
        contactType: 'customer service',
        email: STORE_CONFIG.CONTACT_EMAIL,
        availableLanguage: ['es'],
        areaServed: STORE_CONFIG.GEO_COUNTRY_CODE,
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: STORE_CONFIG.HOURS_OPEN,
          closes: STORE_CONFIG.HOURS_CLOSE,
        },
      },
    ],
    sameAs: STORE_CONFIG.SOCIAL_SAME_AS,
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 1,
      maxValue: 10,
    },
    knowsAbout: [
      'Especias orgánicas',
      'Hierbas aromáticas',
      'Condimentos naturales',
      'Cocina saludable',
      'Gastronomía mediterránea',
    ],
  };
};

// WebSite Schema
export const getWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Solsazón',
    alternateName: 'Solsazon',
    url: siteUrl,
    inLanguage: 'es-ES',
    publisher: {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/products?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

// Product Schema Generator
export const getProductSchema = (product: {
  name: string;
  description: string;
  image: string;
  price: string;
  category: string;
  sku?: string;
  brand?: string;
  rating?: number;
  reviewCount?: number;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  productUrl?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  const offerUrl = product.productUrl || siteUrl;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: {
      '@type': 'ImageObject',
      url: product.image,
      width: 1200,
      height: 630,
    },
    category: product.category,
    brand: {
      '@type': 'Brand',
      name: product.brand || 'Solsazón',
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'EUR',
      availability: `https://schema.org/${product.availability || 'InStock'}`,
      url: offerUrl,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'ES',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 30,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      seller: {
        '@type': 'Organization',
        name: 'Solsazón',
        url: siteUrl,
      },
    },
  };

  if (product.sku) {
    schema.sku = product.sku;
    schema.mpn = product.sku;
  }

  if (product.rating && product.reviewCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return schema;
};

// Article/Blog Schema Generator
export const getArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  url?: string;
  keywords?: string;
  wordCount?: number;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  // Canonical URL: always clean — no query params, no hash, no trailing slash
  const rawUrl = article.url || siteUrl;
  const canonicalUrl = rawUrl.split('?')[0].split('#')[0].replace(/\/$/, '') || rawUrl;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    headline: article.title,
    description: article.description,
    image: {
      '@type': 'ImageObject',
      '@id': `${canonicalUrl}#primaryimage`,
      url: article.image,
      width: 1200,
      height: 630,
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    url: canonicalUrl,
    inLanguage: 'es-ES',
    author: {
      '@type': 'Person',
      name: article.author || 'Equipo Solsazón',
      url: `${siteUrl}/about-us`,
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Solsazón',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
        width: 1200,
        height: 630,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'Solsazón',
      url: siteUrl,
    },
  };

  if (article.keywords) schema.keywords = article.keywords;
  if (article.wordCount) schema.wordCount = article.wordCount;

  return schema;
};

// BlogPosting Schema Generator — optimized for Google News, Discover and rich snippets
// Uses BlogPosting (subtype of Article) which Google specifically recommends for blog content
export const getBlogPostingSchema = (post: {
  title: string;
  description: string;
  image: string;
  /** ISO 8601 date string — e.g. "2026-04-08T10:00:00Z". Must be real ISO, not localized string */
  datePublished: string;
  /** ISO 8601 date string for last modification */
  dateModified?: string;
  author?: string;
  url: string;
  keywords?: string;
  category?: string;
  wordCount?: number;
  /** Reading time in minutes */
  readTime?: number;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  const canonicalUrl = post.url.split('?')[0].split('#')[0].replace(/\/$/, '');

  // headline must be ≤110 chars for Google News eligibility
  const headline = post.title.length > 110 ? `${post.title.slice(0, 107)}...` : post.title;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${canonicalUrl}#blogposting`,
    headline,
    // alternativeHeadline for Google Discover card title
    alternativeHeadline: post.description.length > 110
      ? `${post.description.slice(0, 107)}...`
      : post.description,
    description: post.description,
    // image as ImageObject array — Google News requires at least one image ≥1200px wide
    image: [
      {
        '@type': 'ImageObject',
        '@id': `${canonicalUrl}#primaryimage`,
        url: post.image,
        width: 1200,
        height: 630,
        caption: post.title,
      },
    ],
    // thumbnailUrl — used by Google Discover for the card thumbnail
    thumbnailUrl: post.image,
    datePublished: post.datePublished,
    dateModified: post.dateModified || post.datePublished,
    url: canonicalUrl,
    inLanguage: 'es-ES',
    // author as Person with @id — Google News requires a named author entity
    author: {
      '@type': 'Person',
      '@id': `${siteUrl}/#author-equipo-solsazon`,
      name: post.author || 'Equipo Solsazón',
      url: `${siteUrl}/about-us`,
      worksFor: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Solsazón',
      },
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Solsazón',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
        width: 1200,
        height: 630,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    isPartOf: {
      '@type': 'Blog',
      '@id': `${siteUrl}/blog#blog`,
      name: 'Blog Solsazón — Especias y Bienestar',
      url: `${siteUrl}/blog`,
      publisher: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
      },
    },
  };

  if (post.keywords) schema.keywords = post.keywords;
  if (post.category) schema.articleSection = post.category;
  if (post.wordCount) schema.wordCount = post.wordCount;
  if (post.readTime) {
    schema.timeRequired = `PT${post.readTime}M`;
  }

  return schema;
};

/**
 * getHreflangSet — generates a complete hreflang alternate set for multilingual expansion.
 *
 * Usage: pass the path (e.g. "/blog/mi-articulo") and the supported locales.
 * Returns an array ready to pass to useSEO({ hreflang: getHreflangSet('/blog/mi-articulo') })
 *
 * Current strategy:
 *  - es / es-ES → Spanish (current language, same URL)
 *  - en / en-GB → English (future /en/ prefix, falls back to es URL until translated)
 *  - de         → German  (future /de/ prefix, falls back to es URL until translated)
 *  - x-default  → Spanish canonical (default for unmatched locales)
 *
 * When translations are available, replace the fallback URLs with the real translated URLs.
 */
export const getHreflangSet = (
  path: string,
  options?: { enUrl?: string; deUrl?: string; includeDE?: boolean; includeEN?: boolean }
): Array<{ lang: string; url: string }> => {
  const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com').replace(/\/$/, '');
  const cleanPath = splitLocalePath(path.split('?')[0].split('#')[0]).pathname;
  const esUrl = siteUrl + cleanPath;
  return [
    { lang: 'es', url: esUrl },
    ...(options?.includeEN === false ? [] : [{ lang: 'en', url: options?.enUrl || localizedUrl(esUrl, 'en') }]),
    ...(options?.includeDE === false ? [] : [{ lang: 'de', url: options?.deUrl || localizedUrl(esUrl, 'de') }]),
    { lang: 'x-default', url: esUrl },
  ];
};

// FAQ Schema Generator
export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Breadcrumb Schema Generator
export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`,
    })),
  };
};

// Store / LocalBusiness Schema — enriched for Google Knowledge Panel
// Covers: address, phone, hours, geo, price range, payment, delivery, ratings, offers catalog
export const getStoreSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  return {
    '@context': 'https://schema.org',
    // GroceryStore is a subtype of Store > LocalBusiness — most specific type for spice shops
    '@type': ['GroceryStore', 'Store', 'LocalBusiness'],
    '@id': `${siteUrl}/#localbusiness`,
    name: 'Solsazón — Especias y Hierbas Orgánicas',
    alternateName: ['Solsazon', 'Sol Sazón', 'Especias Solsazón'],
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      '@id': `${siteUrl}/#logo`,
      url: LOGO_URL,
      width: 1200,
      height: 630,
      caption: 'Solsazón — Especias y Hierbas Orgánicas Premium',
    },
    image: [
      LOGO_URL,
      'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/content-images/solsazon-content-images/packs/pack-iniciacion-image.jpg',
    ],
    description: 'Tienda online especializada en hierbas orgánicas certificadas, especias premium y mezclas artesanales. Más de 30 variedades sin aditivos. Envío en 24-48h a toda España desde Almería.',
    // Contact
    telephone: STORE_CONFIG.CONTACT_PHONE_SCHEMA,
    email: STORE_CONFIG.CONTACT_EMAIL,
    // Address — required for Knowledge Panel local pack
    address: {
      '@type': 'PostalAddress',
      streetAddress: STORE_CONFIG.GEO_CITY,
      addressLocality: STORE_CONFIG.GEO_CITY,
      addressRegion: STORE_CONFIG.GEO_REGION,
      postalCode: STORE_CONFIG.GEO_POSTAL_CODE,
      addressCountry: STORE_CONFIG.GEO_COUNTRY_CODE,
    },
    // Geo coordinates — enables map pin in Knowledge Panel
    geo: {
      '@type': 'GeoCoordinates',
      latitude: STORE_CONFIG.GEO_LAT,
      longitude: STORE_CONFIG.GEO_LNG,
    },
    hasMap: `https://maps.google.com/?q=${STORE_CONFIG.GEO_CITY},${STORE_CONFIG.GEO_COUNTRY}`,
    // Opening hours — shown directly in Google Search results
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: STORE_CONFIG.HOURS_OPEN,
        closes: STORE_CONFIG.HOURS_CLOSE,
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: STORE_CONFIG.HOURS_SAT_OPEN,
        closes: STORE_CONFIG.HOURS_SAT_CLOSE,
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Sunday'],
        opens: '00:00',
        closes: '00:00',
      },
    ],
    // Price & payment
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Credit Card, Debit Card, PayPal, Transferencia bancaria',
    // Service area — tells Google which regions this business serves
    areaServed: [
      { '@type': 'Country', name: 'España', '@id': 'https://www.wikidata.org/wiki/Q29' },
      { '@type': 'AdministrativeArea', name: 'Andalucía' },
      { '@type': 'AdministrativeArea', name: 'Madrid' },
      { '@type': 'AdministrativeArea', name: 'Cataluña' },
      { '@type': 'AdministrativeArea', name: 'Valencia' },
    ],
    // Delivery — critical for e-commerce Knowledge Panel
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Catálogo de Especias y Hierbas Orgánicas',
      url: `${siteUrl}/products`,
      numberOfItems: STORE_CONFIG.STAT_PRODUCTS,
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Cúrcuma Orgánica Molida', url: `${siteUrl}/product/curcuma-organica-molida` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Comino Molido Orgánico', url: `${siteUrl}/product/comino-molido-organico` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Pimentón Ahumado Orgánico', url: `${siteUrl}/products` } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Packs de Especias Regalo', url: `${siteUrl}/bundles` } },
      ],
    },
    // Social profiles — used to build the Knowledge Panel entity
    sameAs: STORE_CONFIG.SOCIAL_SAME_AS,
    // Founding info
    foundingDate: '2026',
    foundingLocation: {
      '@type': 'Place',
      name: `${STORE_CONFIG.GEO_CITY}, ${STORE_CONFIG.GEO_REGION}, ${STORE_CONFIG.GEO_COUNTRY}`,
    },
    // Keywords Google uses to understand the business category
    keywords: 'especias orgánicas, hierbas aromáticas, condimentos naturales, tienda especias online España, cúrcuma orgánica, comino natural, pimentón ahumado',
    // Return policy
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: STORE_CONFIG.GEO_COUNTRY_CODE,
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
    // Shipping
  };
};

// Recipe Schema Generator
export const getRecipeSchema = (recipe: {
  name: string;
  description: string;
  image: string;
  prepTime?: number;
  cookTime?: number;
  servings?: number;
  difficulty?: string;
  category?: string;
  ingredients?: string[];
  steps?: string[];
  tags?: string[];
  url?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';
  const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.name,
    description: recipe.description,
    image: recipe.image,
    author: {
      '@type': 'Organization',
      name: 'Solsazón',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Solsazón',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
  };

  if (recipe.prepTime) schema.prepTime = `PT${recipe.prepTime}M`;
  if (recipe.cookTime) schema.cookTime = `PT${recipe.cookTime}M`;
  if (totalTime > 0) schema.totalTime = `PT${totalTime}M`;
  if (recipe.servings) schema.recipeYield = `${recipe.servings} porciones`;
  if (recipe.category) schema.recipeCategory = recipe.category;
  if (recipe.tags && recipe.tags.length > 0) schema.keywords = recipe.tags.join(', ');
  if (recipe.ingredients && recipe.ingredients.length > 0) schema.recipeIngredient = recipe.ingredients;
  if (recipe.steps && recipe.steps.length > 0) {
    schema.recipeInstructions = recipe.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text: step,
    }));
  }
  if (recipe.url) schema.url = recipe.url;

  return schema;
};

// CollectionPage Schema Generator
export const getCollectionPageSchema = (page: {
  name: string;
  description: string;
  url?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.name,
    description: page.description,
    url: page.url ? `${siteUrl}${page.url}` : siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Solsazón',
    },
  };
};

// WebPage Schema Generator (for static pages like privacy, terms, cookies)
export const getWebPageSchema = (page: {
  name: string;
  description: string;
  url: string;
  type?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'FAQPage';
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  return {
    '@context': 'https://schema.org',
    '@type': page.type || 'WebPage',
    name: page.name,
    description: page.description,
    url: `${siteUrl}${page.url}`,
    inLanguage: 'es-ES',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Solsazón',
      url: siteUrl,
    },
  };
};

// ItemList Schema Generator — for /products page rich results
export const getItemListSchema = (items: Array<{
  name: string;
  url: string;
  image: string;
  description?: string;
  price?: string;
  availability?: 'InStock' | 'OutOfStock';
}>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Catálogo de Especias y Hierbas Orgánicas — Solsazón',
    description: 'Lista completa de hierbas y especias orgánicas de alta calidad disponibles en Solsazón',
    url: `${siteUrl}/products`,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        url: `${siteUrl}${item.url}`,
        image: {
          '@type': 'ImageObject',
          url: item.image,
          width: 800,
          height: 800,
        },
        ...(item.description ? { description: item.description } : {}),
        brand: {
          '@type': 'Brand',
          name: 'Solsazón',
        },
        ...(item.price ? {
          offers: {
            '@type': 'Offer',
            price: item.price,
            priceCurrency: 'EUR',
            availability: `https://schema.org/${item.availability || 'InStock'}`,
            url: `${siteUrl}${item.url}`,
          },
        } : {}),
      },
    })),
  };
};

// VideoObject Schema Generator — for blog articles with embedded videos
export const getVideoObjectSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string; // ISO 8601 duration, e.g. "PT5M30S"
  contentUrl?: string;
  embedUrl?: string;
  publisher?: string;
  url?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    url: video.url || siteUrl,
    publisher: {
      '@type': 'Organization',
      name: video.publisher || 'Solsazón',
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
  };

  if (video.duration) schema.duration = video.duration;
  if (video.contentUrl) schema.contentUrl = video.contentUrl;
  if (video.embedUrl) schema.embedUrl = video.embedUrl;

  return schema;
};

// Reviews Schema Generator — individual Review items for rich snippets
export const getReviewsSchema = (reviews: Array<{
  author: string;
  rating: number;
  title: string;
  comment: string;
  date: string;
  verified?: boolean;
}>, productName: string) => {
  return reviews.slice(0, 10).map((review) => ({
    '@type': 'Review',
    name: review.title,
    reviewBody: review.comment,
    datePublished: review.date,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: {
      '@type': 'Person',
      name: review.author,
    },
    itemReviewed: {
      '@type': 'Product',
      name: productName,
    },
    ...(review.verified ? { verificationFactCheckingPolicy: 'https://schema.org/VerifiedPurchase' } : {}),
  }));
};
